import i18next from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import {
    useAllYears,
    useCopyRisksBetweenYears,
} from "../api/hooks/useRiskThemes";
import { useDesignSystem } from "../context/designSystemContext";
import { defaultFilterTypeNumber } from "../types/selectFilter";
import { showErrorToast } from "../utils/errorToast";

export const CopyRisksBetweenYears = () => {
  const { designSystem } = useDesignSystem();
  const { i18n } = useTranslation();
  const [sourceYear, setSourceYear] = useState<defaultFilterTypeNumber | null>(
    null
  );
  const [targetYear, setTargetYear] = useState<defaultFilterTypeNumber | null>(
    null
  );

  const { data: allYears } = useAllYears();
  const copyRisksMutation = useCopyRisksBetweenYears();
  const isLoading = copyRisksMutation.isPending;

  const years = allYears?.map((year: number) => {
    return { value: year, label: year };
  });

  const handleCopy = async () => {
    if (!sourceYear || !targetYear) {
      toast.error(i18next.t("copy_risks_validation_error"));
      return;
    }

    if (sourceYear.value === targetYear.value) {
      toast.error(i18next.t("copy_risks_same_year_error"));
      return;
    }

    try {
      const result = await copyRisksMutation.mutateAsync({
        sourceYear: sourceYear.value,
        targetYear: targetYear.value,
      });

      toast.success(
        i18next.t("copy_risks_success", {
          count: result.copiedCount,
          sourceYear: sourceYear.value,
          targetYear: targetYear.value,
        })
      );

      // Limpar os campos após sucesso
      setSourceYear(null);
      setTargetYear(null);
    } catch (error) {
      showErrorToast(error, i18next.t("copy_risks_error"));
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Ano Origem */}
        <div>
          <label
            className="block font-openSans text-sm font-medium mb-2"
            style={{ color: designSystem?.cssBasics.primaryColor }}
          >
            {i18next.t("copy_risks_source_year")}
          </label>
          <Select
            options={years}
            value={sourceYear}
            onChange={selected => setSourceYear(selected)}
            placeholder={i18next.t("copy_risks_select_source_year")}
            isDisabled={isLoading}
            styles={{
              control: baseStyles => ({
                ...baseStyles,
                minHeight: "40px",
                height: "40px",
              }),
              valueContainer: baseStyles => ({
                ...baseStyles,
                height: "40px",
                padding: "0 8px",
              }),
              input: baseStyles => ({
                ...baseStyles,
                margin: "0px",
                padding: "0px",
              }),
              option: baseStyles => ({
                ...baseStyles,
                fontSize: "14px",
              }),
              singleValue: baseStyles => ({
                ...baseStyles,
                fontSize: "14px",
              }),
            }}
          />
        </div>

        {/* Ano Destino */}
        <div>
          <label
            className="block font-openSans text-sm font-medium mb-2"
            style={{ color: designSystem?.cssBasics.primaryColor }}
          >
            {i18next.t("copy_risks_target_year")}
          </label>
          <Select
            options={years}
            value={targetYear}
            onChange={selected => setTargetYear(selected)}
            placeholder={i18next.t("copy_risks_select_target_year")}
            isDisabled={isLoading}
            styles={{
              control: baseStyles => ({
                ...baseStyles,
                minHeight: "40px",
                height: "40px",
              }),
              valueContainer: baseStyles => ({
                ...baseStyles,
                height: "40px",
                padding: "0 8px",
              }),
              input: baseStyles => ({
                ...baseStyles,
                margin: "0px",
                padding: "0px",
              }),
              option: baseStyles => ({
                ...baseStyles,
                fontSize: "14px",
              }),
              singleValue: baseStyles => ({
                ...baseStyles,
                fontSize: "14px",
              }),
            }}
          />
        </div>
      </div>

      {/* Botão de Copiar */}
      <div className="flex justify-end">
        <button
          onClick={handleCopy}
          disabled={isLoading || !sourceYear || !targetYear}
          className="px-6 py-2 rounded-lg font-openSans font-semibold text-white text-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            backgroundColor: designSystem?.cssBasics.secondaryColor,
          }}
        >
          {isLoading
            ? i18next.t("copy_risks_copying")
            : i18next.t("copy_risks_copy_button")}
        </button>
      </div>

      {/* Informação adicional */}
      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="font-openSans text-sm text-blue-800">
          {i18next.t("copy_risks_info")}
        </p>
      </div>
    </div>
  );
};
